import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomButton from '../../../components/customButton';
import CustomInput from '../../../components/customInput';
import AssetDeviceSelector from '../../../components/assetDeviceSelector';
import EmptyState from '../../../components/emptyState';
import Loading from '../../../components/loading';
import { setAlert } from '../../../redux/dashboard/actions';
import API from '../../../services/api';
import utils from '../../../utils';
import Asset from '../../integrations/asset';

const SiteAssets = ({ projectId, site, updateSite }) => {
  const [assets, setAssets] = useState([]);
  const [displayName, setDisplayName] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deviceSelectorKey, setDeviceSelectorKey] = useState(0);

  const dispatch = useDispatch();

  const loadAssets = useCallback(async () => {
    if (!projectId || !site.id) return;

    setLoading(true);
    const loadedAssets = [];
    const visitedPageTokens = new Set();
    let pageToken = '';

    try {
      do {
        if (visitedPageTokens.has(pageToken)) {
          throw new Error('The Site Assets API returned a repeated page token.');
        }
        visitedPageTokens.add(pageToken);

        const response = await API.getSiteAssets(projectId, site.id, pageToken, '', 'displayName, id', 200);
        loadedAssets.push(...(response.assets || []));
        pageToken = response.nextPageToken || '';
      } while (pageToken);

      setAssets(loadedAssets);
    } catch (error) {
      console.error(error);
      dispatch(setAlert(utils.generateAlert("Could not load this Site's Assets. Try again later.", 'error')));
    } finally {
      setLoading(false);
    }
  }, [dispatch, projectId, site.id]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const resetForm = () => {
    setDisplayName('');
    setSelectedDevices([]);
    setShowCreateForm(false);
    setDeviceSelectorKey(previousKey => previousKey + 1);
  };

  const createAsset = async () => {
    if (!displayName.trim()) return;

    const attachedDeviceIds = selectedDevices.map(device => device.id);
    setSubmitting(true);
    try {
      const asset = await API.createAsset(projectId, site.id, {
        attachedDeviceIds,
        displayName: displayName.trim(),
        siteId: site.id,
        type: 'UNKNOWN'
      });
      const devicesToMove = selectedDevices.filter(device => device.requiresDetach).map(device => device.id);
      if (devicesToMove.length > 0) {
        await API.detachAssetDevicesFromOtherSites(projectId, site.id, asset.id, devicesToMove);
      }
      if (attachedDeviceIds.length > 0) {
        await API.updateAssetAttachedDevices(projectId, site.id, asset.id, attachedDeviceIds);
      }
      dispatch(setAlert(utils.generateAlert('Asset created successfully!', 'success')));
      resetForm();
      await loadAssets();
    } catch (error) {
      console.error(error);
      dispatch(setAlert(utils.generateAlert('Could not create Asset. Please try again.', 'error')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="integrations-container site-assets-management">
      <div className="site-assets-toolbar">
        <h4>Manage the Assets and Devices attached to this Site.</h4>
        {!showCreateForm && (
          <CustomButton classes="btn-primary" title="Add Asset" handleClick={() => setShowCreateForm(true)} />
        )}
      </div>

      {showCreateForm && (
        <div className="site-asset-inline-create">
          <div className="form-group">
            <label>Name</label>
            <CustomInput
              id={`new-site-asset-${site.id}`}
              type="text"
              value={displayName}
              isEditable
              autoFocus
              onChange={event => setDisplayName(event.target.value)}
            />
          </div>
          <div className="form-group asset-device-section">
            <label>Devices (optional)</label>
            <AssetDeviceSelector
              key={deviceSelectorKey}
              projectId={projectId}
              siteId={site.id}
              site={site}
              onSiteUpdated={updateSite}
              selectedDevices={selectedDevices}
              onChange={setSelectedDevices}
              disabled={submitting}
            />
          </div>
          <div className="site-asset-inline-actions">
            <CustomButton classes="btn-secondary" title="Cancel" handleClick={resetForm} disabled={submitting} />
            <CustomButton
              classes="btn-primary"
              title="Create"
              handleClick={createAsset}
              loading={submitting}
              disabled={!displayName.trim()}
            />
          </div>
        </div>
      )}

      <div className="site-assets-list">
        {loading ? (
          <Loading size={8} />
        ) : assets.length > 0 ? (
          assets.map(asset => <Asset key={asset.id} asset={asset} site={site} loadAssets={loadAssets} />)
        ) : (
          <EmptyState icon="deviceOutlineDark" title="This Site has no Assets." details="Create its first Asset." />
        )}
      </div>
    </div>
  );
};

export default SiteAssets;
